import React, { useState, useEffect } from "react";
import Navbar from "./_shared/NavBar";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SectionRepository from "../infrastructure/SectionRepository";
import ChefRepository from "../infrastructure/ChefRepository";
import EventRepository from "../infrastructure/EventRepository";
import { useParams } from "react-router-dom";
import FooterSection from "./_shared/FooterSection";
import Jose from "../assets/chefs/josé.jpg";


const Staff = () => {
    const localizer = momentLocalizer(moment);
    const sectionID = useParams().id;

    const [section, setSection] = useState(null);
    const [chefs, setChefs] = useState([]);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    function processImageField(imageData) {
        if (imageData && imageData.type === "Buffer") {
            // Process the data in chunks to avoid stack overflow
            const data = imageData.data;
            let result = '';
            const chunkSize = 8192; // Process 8K chunks at a time

            for (let i = 0; i < data.length; i += chunkSize) {
                const chunk = data.slice(i, i + chunkSize);
                result += String.fromCharCode.apply(null, chunk);
            }

            return result;
        }
        return imageData; // Return as is if not a Buffer
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const sectionRepository = new SectionRepository();
                const chefRepository = new ChefRepository();
                const eventRepository = new EventRepository();

                const [sectionData, fetchedChefs, fetchedEvents] = await Promise.all([
                    sectionRepository.getSectionById(sectionID),
                    chefRepository.getChefsBySectionId(sectionID),
                    eventRepository.getEventBySection(sectionID),
                ]);

                // Process section image fields
                const processedSection = {
                    ...sectionData,
                    background: processImageField(sectionData.background),
                    second_picture: processImageField(sectionData.second_picture),
                    uniforme: processImageField(sectionData.uniforme)
                };

                // Process chef images if they have any
                const processedChefs = fetchedChefs.map(chef => ({
                    ...chef,
                    // Process any image fields that chefs might have
                    // For example, if chiefs have profile_image:
                    photo: chef.photo ? processImageField(chef.photo) : null
                }));

                setSection(processedSection);
                setChefs(processedChefs);
                setEvents(
                    fetchedEvents.map(event => ({
                        title: event.nom,
                        start: new Date(event.date_debut),
                        end: new Date(event.date_fin),
                        allDay: false,
                    }))
                );

            } catch (err) {
                console.error("Error fetching data:", err);
                setError("Failed to load data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [sectionID]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    if (error) {
        return <div className="min-h-screen flex items-center justify-center">Error: {error}</div>;
    }

    if (!section) {
        return <div className="min-h-screen flex items-center justify-center">No section data available</div>;
    }

    return (
        <div className="bg-gray-100 overflow-x-hidden">
            {/* Hero Section - Taller on mobile */}
            <div
                className="relative bg-cover bg-center h-[35rem] sm:h-[36rem] md:h-[38rem] lg:h-[40rem]"
                style={{ backgroundImage: `url(${require(`../assets${section.background}`)})` }}            >
                <Navbar/>
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center px-4">
                    <h1 className="font-anton text-4xl sm:text-5xl md:text-6xl lg:text-8xl text-white font-extrabold italic text-center">
                        {section.Nom}
                    </h1>
                </div>
            </div>

            {/* Description Section with Overlapping Images visible on all screen sizes */}
            <section className="container mx-auto my-6 md:my-10 lg:my-12 px-4">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Description Container */}
                    <div className="w-full lg:w-1/2 px-0 md:px-4 flex items-center justify-center text-left">
                        <p className="text-gray-700 leading-relaxed text-base sm:text-lg md:text-xl lg:text-2xl">
                            {section.description || "Section description here."}
                        </p>
                    </div>

                    {/* Overlapping images container - now visible on all screen sizes */}
                    <div className="w-full lg:w-1/2 mt-6 lg:mt-0 relative h-64 sm:h-80 md:h-96 lg:h-[28rem]">
                        {/* First image - positioned as base layer */}
                        <img
                            className="rounded-lg shadow-lg w-full h-full object-cover absolute left-0 top-0"
                            src={require(`../assets${section.second_picture}`)}
                            alt="groupe"
                        />
                    </div>
                </div>
            </section>


            {/* Staff Section */}
            <section className="bg-gray-800 py-12 md:py-16 lg:py-32 text-white flex flex-col items-center justify-center">
                <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl font-anton text-center mb-6 md:mb-8 lg:mb-12 italic">Le Staff</h2>
                <div className="container px-4 md:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
                        {chefs.map(chef => (
                            <StaffCard
                                key={chef.id}
                                name={chef.nom}
                                age={chef.age || "N/A"}
                                studies={chef.etudes || "N/A"}
                                since={chef.annee_arrivee || "N/A"}
                                contact={chef.contact || "N/A"}
                                role={chef.role || "N/A"}
                                image={chef.photo || null}
                            />
                        ))}
                    </div>
                </div>
            </section>

            {/* Calendar Section */}
            <section className="container mx-auto my-8 sm:my-10 md:my-12 px-4 md:px-6">
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-anton text-green-700 text-center mb-4 md:mb-6 lg:mb-8 italic">
                    Planning
                </h2>
                <div className="bg-white p-2 md:p-4 rounded-lg shadow-lg">
                    <BigCalendar
                        localizer={localizer}
                        events={events}
                        startAccessor="start"
                        endAccessor="end"
                        style={{height: 400, minWidth: 300}}
                        toolbar={true}
                        views={['month', 'week', 'day']}
                    />
                </div>
            </section>

            <section className="container mx-auto my-8 sm:my-10 md:my-12 px-4 md:px-6 w-full">
                <div className="flex flex-col gap-6 sm:gap-8 md:gap-12 lg:gap-16 items-center">
                    {/* Uniform Section */}
                    <div className="w-full">
                        <h2 className="text-green-700 font-anton text-3xl sm:text-4xl md:text-5xl lg:text-6xl italic mb-4">
                            Uniformes
                        </h2>
                        <div className="flex flex-col md:flex-row gap-6 md:gap-10 lg:gap-20">
                            <img
                                src={require(`../assets${section.uniforme}`)}
                                alt="Uniformes"
                                className="rounded-lg shadow-lg w-full md:w-1/2 h-auto object-cover"
                            />
                            <div className="flex flex-col justify-around mt-6 md:mt-0">
                                <p className="text-base sm:text-lg md:text-xl lg:text-2xl">
                                    <strong>Foulard : Vert & bords gris</strong>
                                    <br/>
                                    <br/>
                                    L'entiereté des écussons, chemise, bandes,... , sont en vente chez Alpi Sport (Rue
                                    de Fragnée 170, 4000 Liège) ou sur le site de la scouterie (
                                    <a
                                        href="https://www.lascouterie-economats.be/"
                                        className="text-blue-500 break-words"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        https://www.lascouterie-economats.be/
                                    </a>
                                    )
                                </p>
                                <div className="mt-6 md:mt-0">
                                    <h2 className="text-green-700 font-anton text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-4 italic">
                                        Informations pratiques
                                    </h2>
                                    <p className="text-gray-700 text-sm sm:text-base md:text-lg mb-2">
                                        <span className="font-bold">Adresse e-mail: </span>
                                        {section.mail}
                                    </p>
                                    <p className="text-gray-700 text-sm sm:text-base md:text-lg">
                                        <span className="font-bold">Compte bancaire: </span>
                                        {section.compte}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer Section */}
            <FooterSection/>
        </div>
    );
};

const StaffCard = ({name, age, studies, since, contact, role, image}) => {
    return (
        <div className="flex flex-row items-center gap-4 sm:gap-6 md:gap-8 bg-gray-700 bg-opacity-50 rounded-lg p-4 hover:bg-gray-600 transition-colors duration-300">
            <img
                src={
                    image
                        ? require(`../assets${image}`) // Force Webpack à processer l'image
                        : Jose
                }
                alt={name}
                className="w-24 h-32 xs:w-28 xs:h-40 sm:w-36 sm:h-48 md:w-40 md:h-56 lg:w-48 lg:h-64 xl:w-52 xl:h-72 rounded-lg object-cover shadow-lg flex-shrink-0"
            />
            <div className="ml-3 sm:ml-4 md:ml-6">
                <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-anton italic">{name}</h3>
                <p className="text-gray-300 text-sm sm:text-base">Études: {studies}</p>
                <p className="text-gray-300 text-sm sm:text-base">À Ste-Vé depuis: {since}</p>
                <p className="text-gray-300 text-sm sm:text-base">Contact: {contact}</p>
                <p className="text-gray-300 text-sm sm:text-base">Rôle: {role}</p>
            </div>
        </div>
    );
};

export default Staff;